import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField, InputAdornment } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useRenameFile } from '../../../hooks/uploads/useRenameFile';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';

export default function FileRenameForm({ successCallback = null, attachment = null, mode = "edit", open, onClose }) {
  const { t } = useTranslation('common'); 

  // Split filename into base and extension
  const getParts = (filename) => {
    if (!filename) return { base: "", ext: "" };
    const lastDot = filename.lastIndexOf(".");
    if (lastDot === -1) return { base: filename, ext: "" };
    return {
      base: filename.substring(0, lastDot),
      ext: filename.substring(lastDot) // includes the dot
    };
  };

  const initialParts = getParts(attachment ? attachment.filename : "");
  const [baseName, setBaseName] = React.useState(initialParts.base);
  const [extension, setExtension] = React.useState(initialParts.ext);

  const { rename } = useRenameFile(successCallback);
  const { showSnackbar } = useSnackbar();

  React.useEffect(() => {
    const parts = getParts(attachment ? attachment.filename : "");
    setBaseName(parts.base);
    setExtension(parts.ext);
  }, [attachment, open]);

  const INVALID_FILENAME_CHARS = /[\\\/:*?"<>|;]/;  // add more if needed

  const handleSave = async () => {
    if (!baseName.trim()) {
      showSnackbar(t('files.messages.filename_required'), "warning");
      return;
    }

    if (INVALID_FILENAME_CHARS.test(baseName)) {
      showSnackbar(t('files.messages.filename_invalid'), "error");
      return;
    }

    try {
      if (mode === "edit" && attachment) {
        await rename(attachment.id, { 
          filename: baseName + extension,
        });
        showSnackbar(t('files.messages.rename_success'), "success");
      }
      setBaseName("");   // clear field after save
      setExtension("");
      onClose();         // close dialog
    } catch (err) {
      showSnackbar(t('files.messages.rename_fail'), "error");
    }
  };

  const handleCancel = () => {
    setBaseName("");   
    setExtension("");
    onClose();
  };

  return (
    <Dialog onClose={handleCancel} open={open} disableRestoreFocus>
      <DialogTitle>Rename Attachment</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1}>
          <TextField
            autoFocus
            label="Filename"
            variant="outlined"
            value={baseName}
            onChange={(e) => setBaseName(e.target.value)}
            fullWidth
            inputProps={{ maxLength: 50 }}
            helperText={`${baseName.length}/50 characters`}
            slotProps={{
                input: {
                endAdornment: (
                    <span style={{ color: 'text.secondary', marginLeft: 4 }}>
                    {extension}
                    </span>
                ),
                },
            }}
            />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleSave}>Save</Button>
        <Button onClick={handleCancel}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}